import sortSectionFields from '../../../helpers/questionnaire/sortSectionFields';
import notDeleted from '../../../helpers/notDeleted';
import filterFieldsForMoment from '../../../helpers/questionnaire/filterFieldsForMoment';
import describeShowWhen from '../../../helpers/questionnaire/describeShowWhen';
import { scaleBoundsFor } from './validateScaleAnswer';
import scaleStartLabel from '../../../helpers/questionnaire/scaleStartLabel';

/**
 * @param {object} db - Firestore client
 * @param {Array<object>} questionaires - De Questionnaires-docs van deze sessie
 * @param {Array<string>} [excludedFieldIDs] - Vragen die op dit meetmoment niet
 *   gesteld worden (`MeasureMoments.ExcludedFieldIDs`). Leeg = alles aan.
 * @returns {Promise<string>} JSON met de vragen, gegroepeerd per sectie
 */
const getQuestionnaire = (db, questionaires, excludedFieldIDs ) => {

    // Optie-, rij- en kolomteksten die onderweg langskomen, zodat een regel
    // straks als leesbare zin bij de vraag kan staan in plaats van als uuid.
    const labels = {};

    // Answer options
  const getAnswerOptions = async (questionId, type, docData) => {
    if (type === 'multiple-one' || type === 'multiple-multiple') {
      const snapshot = await db.collection('MultipleQuestionOptions')
        .where('Field', '==', questionId)
        .orderBy('Position', 'asc')
        .get();
      return snapshot.docs.filter(doc => notDeleted(doc.data())).map(doc => {
        labels[doc.data().ID] = doc.data().Option;
        return {
          ID: doc.data().ID,
          Value: doc.data().Option,
          QuestionnaireFieldID: doc.data().Field
        };
      });
    } else if (type === 'scale') {
      // Zelfde grenzen als bij het opslaan (validateScaleAnswer); 0 blijft 0.
      const { min, max } = scaleBoundsFor(docData);
      return {
        min,
        minLabel: scaleStartLabel(docData) || 'Min',
        max,
        maxLabel: docData.ReachEndLabel || 'Max',
      };
    } else if (type === 'matrix-one' || type === 'matrix-multiple') {
      const rowSnapshot = await db.collection('MatrixQuestionRows')
        .where('Field', '==', questionId)
        .orderBy('Position', 'asc')
        .get();
      const columnSnapshot = await db.collection('MatrixQuestionColumns')
        .where('Field', '==', questionId)
        .orderBy('Position', 'asc')
        .get();

      const columnArray = columnSnapshot.docs.filter(doc => notDeleted(doc.data())).map(doc => {
        labels[doc.data().ID] = doc.data().Title;
        return { ID: doc.data().ID, Option: doc.data().Title };
      });

      return rowSnapshot.docs.filter(doc => notDeleted(doc.data())).map(doc => {
        labels[doc.data().ID] = doc.data().Title;
        return {
          ID: doc.data().ID,
          Value: doc.data().Title,
          ValueRight: doc.data().TitleRight || '',
          columns: columnArray
        };
      });
    }
    return [];
  };

    // Build questionnaires JSON, grouped by section.
    // `items` is an ordered mix of:
    //   { kind: 'section', sectionId, sectionTitle, questions: [...] }
    //   { kind: 'question', ...question }
    // Section markers are NOT questions — they only group the questions inside them.
    // Position is a running counter over the actual questions (1..N), so progress works.
  const questions = async () => {
    if (!questionaires) return '';
    const array = [];

    for (const q of questionaires) {
      const object = { title: q.Title, description: q.Description, items: [] };

      const fieldsSnap = await db.collection('QuestionnaireFields')
        .where('QuestionnaireID', '==', q.ID)
        .orderBy('Position', 'asc')
        .get();
      // Zelfde filter als het invulscherm: een op dit meetmoment uitgezette
      // vraag mag ook via de interviewer niet gesteld worden.
      const all = filterFieldsForMoment(
        fieldsSnap.docs.map(d => d.data()).filter(notDeleted),
        excludedFieldIDs
      );

      // Section markers + top-level questions, in Position order.
      const topLevel = all.filter(f => (f.SectionID || '') === '');

      // Assign positions synchronously (document order), then resolve every
      // question's AnswerOptions in parallel — avoids one serial round-trip per question.
      let pos = 0;
      const makeDescriptor = (f, sectionTitle) => {
        pos += 1;
        return {
          f,
          question: {
            Position: pos,
            Question: f.Question,
            Type: f.Type,
            ID: f.ID,
            QuestionnaireID: f.QuestionnaireID,
            SectionID: f.SectionID || '',
            SectionTitle: sectionTitle || '',
            AnswerOptions: []
          }
        };
      };

      const skeletons = topLevel.map(field => {
        if (field.Type === 'section') {
          const children = sortSectionFields(all.filter(c => (c.SectionID || '') === field.ID));
          return { kind: 'section', sectionId: field.ID, sectionTitle: field.Question, descriptors: children.map(c => makeDescriptor(c, field.Question)) };
        }
        return { kind: 'question', descriptor: makeDescriptor(field, '') };
      });

      const allDescriptors = skeletons.flatMap(s => s.kind === 'section' ? s.descriptors : [s.descriptor]);
      await Promise.all(allDescriptors.map(async d => {
        d.question.AnswerOptions = await getAnswerOptions(d.f.ID, d.f.Type, d.f);
      }));

      // Conditionele vragen dragen hun voorwaarde als leesbare zin mee. Het
      // model kan die volgen; de harde grens ligt bij het opslaan, want een
      // model dat de regel negeert mag geen document opleveren.
      // AI-CONTEXT: See docs/features/conditional-questions.md
      allDescriptors.forEach(d => {
        const described = describeShowWhen(d.f, all, labels);
        if (described.state === 'ok') d.question.AskOnlyIf = described.text;
      });

      skeletons.forEach(s => {
        if (s.kind === 'section') {
          object.items.push({ kind: 'section', sectionId: s.sectionId, sectionTitle: s.sectionTitle, questions: s.descriptors.map(d => d.question) });
        } else {
          object.items.push({ kind: 'question', ...s.descriptor.question });
        }
      });

      array.push(object);
    }

    return JSON.stringify(array);
  };

  return questions();


}

export default getQuestionnaire;